import { fromSystemFields, isFieldRequired, toSystemFields } from '@features/ContactFieldsSettings/fields';
import { formatWidgetOffset, normalizeWidgetAlignment, parseWidgetOffset } from '@constants/widget';
import {
    BUTTON_KEYS,
    LOCATIONS_MODE,
    MAX_WELCOME_BUTTONS,
    OFFLINE_MESSAGE_FIELDS,
    PLUSPOINT_EMAIL,
    WELCOME_MESSAGE_FIELD,
    fillTextRow,
    getDefaultWelcomeMessage,
    normalizeAllowedSites,
    welcomeButtonFields,
} from '@constants/liveChat';
import { toEditedBusinessHours, toStoredBusinessHours } from '@helpers/business-hours';
import { isSurveyTextComplete } from '@helpers/surveyLanguages';

const toTextRow = ({ language, welcomeMessage, offlineMessage, buttons }) =>
    fillTextRow({
        language,
        welcomeMessage,
        offlineMessage,
        ...Object.fromEntries(BUTTON_KEYS.map((key, index) => [key, buttons?.[index]])),
    });

const rowButtons = (row, count, isFallbackRow) => {
    const labels = BUTTON_KEYS.slice(0, count).map(key => (row[key] || '').trim());
    if (isFallbackRow) return labels.filter(Boolean);
    while (labels.length && !labels[labels.length - 1]) labels.pop();
    return labels;
};

export const buildLiveChatTexts = language => [
    toTextRow({ language, welcomeMessage: getDefaultWelcomeMessage(language) }),
];

export const requiredContactFields = values => ({
    phoneRequired: isFieldRequired(values, 'phone'),
    emailRequired: isFieldRequired(values, 'email'),
});

export const validateLiveChatValues = values => {
    const { phoneRequired, emailRequired } = requiredContactFields(values);
    return phoneRequired || emailRequired ? null : 'phoneOrEmailRequired';
};

export const toLiveChatPayload = (values, { id, timeZone, referenceDate = new Date() }) => {
    const { phoneRequired, emailRequired } = requiredContactFields(values);
    const notify = !!values.notifyAboutMissingConversations && (phoneRequired || emailRequired);
    const followUp = notify ? values.followUpChannelId : '';
    const buttonCount = Math.min(values.buttonCount || 0, MAX_WELCOME_BUTTONS);
    const assignLocation = !!values.assignLocation;

    return {
        id,
        name: values.name,
        teamId: values.teamId,
        locationId: null,
        brandId: values.brandId || null,
        labels: values.labels,
        liveChatParameters: {
            assignLocation,
            locations:
                assignLocation && values.locationsMode === LOCATIONS_MODE.SELECTED
                    ? (values.locations || []).map(item => item.id)
                    : [],
            followUpChannelId: followUp && followUp !== PLUSPOINT_EMAIL ? followUp : null,
            useDefaultEmailChannel: followUp === PLUSPOINT_EMAIL,
            notifyAboutMissingConversations: notify,
            texts: (values.texts || []).map((row, index) => ({
                language: row.language,
                welcomeMessage: row.welcomeMessage || '',
                offlineMessage: row.offlineMessage || '',
                buttons: rowButtons(row, buttonCount, index === 0),
            })),
            welcomePopupEnabled: !!values.welcomePopupEnabled,
            workingHoursEnabled: !!values.workingHoursEnabled,
            workingHours: toStoredBusinessHours(values.workingHours, timeZone, referenceDate),
            alignment: normalizeWidgetAlignment(values.alignment),
            offsetX: parseWidgetOffset(values.offsetX),
            offsetY: parseWidgetOffset(values.offsetY),
            allowedSites: normalizeAllowedSites(values.allowedSites),
            fields: toSystemFields(values),
        },
    };
};

export const toLiveChatFormValues = (
    channel,
    { currentLocale, timeZone, locations, mapSelectedLocationsToFormValue, referenceDate = new Date() },
) => {
    const params = channel?.liveChatParameters || {};
    const legacyLocationId = !channel?.brandId && channel?.locationId ? channel.locationId : null;
    const brandId =
        channel?.brandId ||
        (legacyLocationId && locations.find(location => location.companyId === legacyLocationId)?.brand?.id) ||
        '';
    const legacyLocationIds = legacyLocationId && brandId ? [legacyLocationId] : [];
    const locationIds = legacyLocationId ? legacyLocationIds : params.locations || [];
    const rows = params.texts?.length
        ? params.texts
        : [{ language: currentLocale, welcomeMessage: getDefaultWelcomeMessage(currentLocale) }];

    return {
        name: channel?.name || '',
        brandId,
        labels: channel?.labels || [],
        assignLocation: !!legacyLocationId || !!params.assignLocation,
        locationsMode: locationIds.length ? LOCATIONS_MODE.SELECTED : LOCATIONS_MODE.ALL,
        locations: mapSelectedLocationsToFormValue(locationIds) || [],
        notifyAboutMissingConversations: !!params.notifyAboutMissingConversations,
        followUpChannelId: params.useDefaultEmailChannel ? PLUSPOINT_EMAIL : params.followUpChannelId || '',
        texts: rows.map(toTextRow),
        buttonCount: Math.min(rows[0].buttons?.length || 0, MAX_WELCOME_BUTTONS),
        welcomePopupEnabled: params.welcomePopupEnabled ?? true,
        workingHoursEnabled: !!params.workingHoursEnabled,
        workingHours: toEditedBusinessHours(params.workingHours, timeZone, referenceDate),
        ...fromSystemFields(params.fields),
        alignment: normalizeWidgetAlignment(params.alignment),
        offsetX: formatWidgetOffset(params.offsetX),
        offsetY: formatWidgetOffset(params.offsetY),
        allowedSites: params.allowedSites || [],
    };
};

export const isDefaultLiveChatTextComplete = (texts, { workingHoursEnabled, buttonCount }) => {
    const row = texts?.[0];
    if (!row) return false;
    const fields = [
        WELCOME_MESSAGE_FIELD,
        ...(workingHoursEnabled ? OFFLINE_MESSAGE_FIELDS : []),
        ...welcomeButtonFields(buttonCount || 0),
    ];
    return isSurveyTextComplete(row, fields);
};

export const isFollowUpHiddenFromEditor = (liveChat, followUpChannelId, visibleChannels) =>
    !!followUpChannelId &&
    !liveChat?.followUpChannelMissing &&
    !visibleChannels.some(channel => channel.id === followUpChannelId);
